// Fold standalone safety blocks of a bot answer (2026-09-30).
//
// Maintenance and fault answers often carry a general safety paragraph the question
// did not ask for ("Sicherheitsvorgaben vor Arbeiten an der Hydraulik: … gegen
// Wiedereinschalten sichern"). Prompt/tag experiments could not remove it reliably and
// nothing may be DROPPED on industrial machines, so the chat only shows it folded under
// a visible amber line. Pure string work, no React: splitSafetyBlocks(md) returns
// [{ kind: 'main' | 'safety', md, title?, danger? }].
//
// A safety block is
//   * a standalone heading line (`**…**` or `# …`) whose text is a safety heading
//     ("Sicherheitsvorgaben …", "Zugehörige Sicherheitsvorgaben …", "Sicherheits- und
//     Verhaltenshinweise", "Sicherheit vor/bei …"), up to the next standalone heading, or
//   * a paragraph that starts with a bold label "**Sicherheit:**".
// Inline warnings inside other text ("Vorsicht: Verbrennungsgefahr …") are never folded,
// and nothing is folded when it would leave the answer empty.
//
// 2026-10-19: also the notes tail of lookup answers — headings "Wichtige Hinweise",
// "Wichtige Sicherheit", "Sicherheitswarnung …", "Sicherheitsrelevante Hinweise/Vorgaben …"
// and paragraphs led by a bold "Wichtig:", "Warnung:", "Achtung:", "Vorsicht:", "GEFAHR:",
// "Sicherheitsrelevant …:" label. These wider forms are skipped in the first paragraph (the
// direct answer) and in answers with numbered steps (how-to/fault steps keep their warnings).
// Content headings like "Wichtigste Prüfschritte", "Wichtige Randbedingungen" or
// "Hinweise aus der Erklärung" never match: the heading text is matched as a whole.

const HEADING_LINE_RX = /^\s{0,3}(?:#{1,6}\s+(.+?)\s*#*|\*\*([^*\n]{1,100})\*\*:?)\s*$/;
const SAFETY_HEADING_RX =
  /sicherheits(?:-\s*und\s+[\p{L}]+)?[\s-]?(?:vorgabe|hinweis|maßnahme|massnahme|regel|anweisung|information)|sicherheitsrelevante?\s+(?:vorgabe|hinweis|maßnahme|massnahme|regel|anweisung|information)|^sicherheit\s+(?:vor|bei|beim|an|zu|für)\b/iu;
const INLINE_LABEL_RX = /^\s*\*\*Sicherheit(?:shinweise?)?:?\*\*:?\s+\S/i;
const WIDE_HEADING_RX =
  /^(?:wichtige[rs]?\s+(?:sicherheits)?hinweise?|wichtige\s+sicherheit(?:shinweise?)?|sicherheitswarnung(?:\s.*)?|sicherheitsrelevante?\s+(?:hinweise?|vorgaben?|einschränkung(?:en)?)(?:\s.*)?)$/iu;
const WIDE_LABEL_RX =
  /^\s*\*\*((?:Sicherheit\p{L}*)(?:\s[^*\n:]{1,60})?|Wichtig|Warnung|Achtung|Vorsicht|Gefahr)\s*(?::\*\*|\*\*:)\s+\S/iu;
const STEP_RX = /^\s{0,3}\d+[.)]\s+\S/gm;
export const DANGER_RX = /\b(?:GEFAHR|LEBENSGEFAHR)\b|Lebensgefahr|\bTod\b/;

function headingText(line) {
  const m = HEADING_LINE_RX.exec(line);
  return m ? (m[1] || m[2] || '').trim() : null;
}

// A question that asks for safety or precautions ("welche Sicherheitseinrichtungen …",
// "was muss ich bei der Inbetriebnahme beachten", "Not-Aus …") wants these notes: no fold.
const SAFETY_QUESTION_RX = /sicher|schutz|gefahr|warn|vorsicht|not-?\s?(?:halt|aus)|beacht|achten|achtung|risik|verletz|unfall/i;

export function splitSafetyBlocks(md, opts = {}) {
  if (typeof md !== 'string' || md.length === 0 || !/sicherheit|wichtig|warnung|achtung|vorsicht|gefahr/i.test(md)) {
    return [{ kind: 'main', md: md || '' }];
  }
  if (opts.question && SAFETY_QUESTION_RX.test(opts.question)) {
    return [{ kind: 'main', md }];
  }
  const lines = md.split('\n');
  const wide = (md.match(STEP_RX) || []).length < 3;
  const segs = [];
  let main = [];
  const flushMain = () => {
    if (main.some((l) => l.trim() !== '')) segs.push({ kind: 'main', md: main.join('\n') });
    main = [];
  };
  let i = 0;
  while (i < lines.length) {
    const line = lines[i];
    const h = headingText(line);
    const hasLead = segs.length > 0 || main.some((l) => l.trim() !== '');
    const wideHeading = wide && hasLead && h !== null && WIDE_HEADING_RX.test(h.replace(/:$/, '').trim());
    const wideLabel = wide && hasLead && h === null ? WIDE_LABEL_RX.exec(line) : null;
    if (h !== null && (SAFETY_HEADING_RX.test(h) || wideHeading)) {
      let j = i + 1;
      while (j < lines.length && headingText(lines[j]) === null) j += 1;
      const body = lines.slice(i + 1, j).join('\n').trim();
      if (body) {
        flushMain();
        segs.push({ kind: 'safety', md: body, title: h.replace(/:$/, ''), danger: DANGER_RX.test(body) });
        i = j;
        continue;
      }
    } else if (INLINE_LABEL_RX.test(line) || wideLabel) {
      let j = i + 1;
      while (j < lines.length && lines[j].trim() !== '') j += 1;
      flushMain();
      const body = lines.slice(i, j).join('\n');
      const title = wideLabel && !INLINE_LABEL_RX.test(line) ? wideLabel[1].trim() : 'Sicherheit';
      segs.push({ kind: 'safety', md: body, title, danger: DANGER_RX.test(body) });
      i = j;
      continue;
    }
    main.push(line);
    i += 1;
  }
  flushMain();
  // Never fold an answer that has nothing else to show.
  const hasMain = segs.some((s) => s.kind === 'main' && s.md.trim() !== '');
  if (!hasMain) return [{ kind: 'main', md }];
  return segs;
}

export function summaryLabel(seg) {
  const base = seg.title || 'Sicherheitshinweise';
  return `${base}${seg.danger ? ' – enthält GEFAHR-Hinweis' : ''}`;
}
